import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsPaper } from '../common/basic/QcsPaper';
import { QcsTableContainer } from '../common/basic/QcsTableContainer';
import { QcsTableHead } from '../common/basic/QcsTableHead';
import { QcsTableRow } from '../common/basic/QcsTableRow';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTableBody } from '../common/basic/QcsTableBody';
import { QcsTooltip } from '../common/basic/QcsTooltip';
import { ShowQcsTableContent } from '../ShowQcsTableContent';
import { IntegrationStatusChip } from './IntegrationStatusChip';
import { IntegrationKeyExpiry } from './IntegrationKeyExpiry';
import { API_ACCESS_PATH, formatScopes, isExpired, isExpiringSoon } from './integrationUtils';
import { formatDate } from '../../utils/date';
import { apiClientService } from '@api/services/services';
import { resources } from '@api/resources';
import { ApiClientListDto } from '@api/models/apiClient/ApiClientListDto';
import { ApiScopeDto } from '@api/models/apiClient/ApiScopeDto';

/** The "API access" tab: partner systems that call the S4E API. */
export const IntegrationApiClients: FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [apiClients, setApiClients] = useState<ApiClientListDto[]>([]);
  const [scopeCatalogue, setScopeCatalogue] = useState<ApiScopeDto[]>([]);

  useEffect(() => {
    apiClientService.list().then((res) => setApiClients(res.data));
    apiClientService.getScopes().then((res) => setScopeCatalogue(res.data));
  }, []);

  const getWarnings = (item: ApiClientListDto) =>
    [
      isExpired(item.nearestKeyExpiresAt) && t('integration.apiAccess.warnings.keyExpired'),
      isExpiringSoon(item.nearestKeyExpiresAt) && t('integration.apiAccess.warnings.keyExpiresSoon'),
    ].filter((x): x is string => !!x);

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <QcsTypography variant="body2" sx={{ maxWidth: '40rem' }}>
          {t('integration.apiAccess.description')}
        </QcsTypography>
        <QcsButton variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${API_ACCESS_PATH}/new`)}>
          {t('integration.apiAccess.add')}
        </QcsButton>
      </Box>
      <QcsPaper>
        <QcsTableContainer>
          <ShowQcsTableContent resource={[resources.listApiClients, resources.apiClientScopes]} data={apiClients}>
            <QcsTableHead>
              <QcsTableRow>
                <QcsTableCell>{t('integration.apiAccess.name')}</QcsTableCell>
                <QcsTableCell hideOnMobile>{t('integration.apiAccess.authMethod.title')}</QcsTableCell>
                <QcsTableCell>{t('integration.apiAccess.statusLabel')}</QcsTableCell>
                <QcsTableCell hideOnMobile>{t('integration.apiAccess.scopes.title')}</QcsTableCell>
                <QcsTableCell hideOnMobile>{t('integration.apiAccess.lastUsedAt')}</QcsTableCell>
                <QcsTableCell hideOnMobile>{t('integration.apiAccess.key.expiresAt')}</QcsTableCell>
                <QcsTableCell sx={{ width: '3rem' }}>{null}</QcsTableCell>
              </QcsTableRow>
            </QcsTableHead>
            <QcsTableBody>
              {apiClients.map((item) => {
                const warnings = getWarnings(item);

                return (
                  <QcsTableRow key={item.id} hover onClick={() => navigate(`${API_ACCESS_PATH}/${item.id}`)} sx={{ cursor: 'pointer' }}>
                    <QcsTableCell>{item.name}</QcsTableCell>
                    <QcsTableCell hideOnMobile>{t(`integration.apiAccess.authMethod.${item.authMethod}`)}</QcsTableCell>
                    <QcsTableCell>
                      <IntegrationStatusChip status={item.status} />
                    </QcsTableCell>
                    <QcsTableCell hideOnMobile>
                      {formatScopes(t, item.scopes, scopeCatalogue).map((scope) => (
                        <div key={scope}>{scope}</div>
                      ))}
                    </QcsTableCell>
                    <QcsTableCell hideOnMobile>
                      {item.lastUsedAt ? formatDate(i18n, item.lastUsedAt, true) : t('integration.apiAccess.never')}
                    </QcsTableCell>
                    <QcsTableCell hideOnMobile>
                      {item.nearestKeyExpiresAt && <IntegrationKeyExpiry expiresAt={item.nearestKeyExpiresAt} />}
                    </QcsTableCell>
                    <QcsTableCell>
                      {warnings.length > 0 && (
                        <QcsTooltip
                          title={warnings.map((warning) => (
                            <div key={warning}>{warning}</div>
                          ))}
                        >
                          <WarningAmberIcon color="warning" />
                        </QcsTooltip>
                      )}
                    </QcsTableCell>
                  </QcsTableRow>
                );
              })}
            </QcsTableBody>
          </ShowQcsTableContent>
        </QcsTableContainer>
      </QcsPaper>
    </Box>
  );
};
